import { useState, useMemo } from 'react';

import type { EmpresaPlataforma } from '@/mocks/backoffice/empresas';
import {
  participantesCampanhas,
  getStatusColor,
  getStatusLabel,
  type ParticipanteCampanha,
} from '@/mocks/backoffice/participantesCampanhas';
import { campanhasBackoffice } from '@/mocks/backoffice/campanhasBackoffice';
import { configuracoesCampanhas, valoresPadraoPlataforma } from '@/mocks/backoffice/configuracoesCampanhas';
import {
  participanteCampanhaConfigs,
  getConfigsPorParticipante,
  getConfigParticipanteCampanha,
  temConfigPersonalizada,
  salvarConfigParticipanteCampanha,
  removerConfigParticipanteCampanha,
  type ParticipanteCampanhaConfig,
} from '@/mocks/backoffice/participanteCampanhaConfig';
import { registrarAuditoria } from '@/mocks/backoffice/auditoriaHelpers';

interface EmpresaPremiadosConfigProps {
  empresa: EmpresaPlataforma;
  onShowToast: (msg: string, type: 'success' | 'error') => void;
}

type CampoPersonalizado = 'limiteResgateDiario' | 'limiteResgateMensal' | 'taxaProcessamentoPix' | 'prazoAprovacaoSaque';

interface CampoConfigRow {
  chave: CampoPersonalizado;
  label: string;
  tipo: 'numero' | 'porcentagem';
  descricao: string;
  unidade: string;
}

const camposPersonalizaveis: CampoConfigRow[] = [
  { chave: 'limiteResgateDiario', label: 'Limite Resgate Diário', tipo: 'numero', descricao: 'Valor máximo de resgate PIX por dia (R$)', unidade: 'R$' },
  { chave: 'limiteResgateMensal', label: 'Limite Resgate Mensal', tipo: 'numero', descricao: 'Valor máximo de resgate PIX por mês (R$)', unidade: 'R$' },
  { chave: 'taxaProcessamentoPix', label: 'Taxa Processamento PIX', tipo: 'porcentagem', descricao: 'Taxa percentual cobrada sobre cada resgate PIX', unidade: '%' },
  { chave: 'prazoAprovacaoSaque', label: 'Prazo Aprovação Saque', tipo: 'numero', descricao: 'Prazo máximo em horas para aprovação de saques', unidade: 'h' },
];

interface ParticipanteAgregado {
  id: string;
  nome: string;
  email: string;
  cpf: string;
  status: 'ativo' | 'pendente' | 'bloqueado';
  pontosDisponiveis: number;
  pontosAcumulados: number;
  nivel: number;
  campanhas: { campanhaId: string; campanhaNome: string; configPersonalizada: boolean }[];
}

function agruparParticipantesPorEmpresa(empresaId: string): ParticipanteAgregado[] {
  const parts = participantesCampanhas.filter((p) => p.empresaId === empresaId);
  const map = new Map<string, ParticipanteAgregado>();

  parts.forEach((p) => {
    const campanha = campanhasBackoffice.find((c) => c.id === p.campanhaId);
    const configPers = temConfigPersonalizada(p.id, p.campanhaId);

    if (map.has(p.id)) {
      map.get(p.id)!.campanhas.push({
        campanhaId: p.campanhaId,
        campanhaNome: campanha?.nome || p.campanhaId,
        configPersonalizada: configPers,
      });
    } else {
      map.set(p.id, {
        id: p.id,
        nome: p.nome,
        email: p.email,
        cpf: p.cpf,
        status: p.status,
        pontosDisponiveis: p.pontosDisponiveis,
        pontosAcumulados: p.pontosAcumulados,
        nivel: p.nivel,
        campanhas: [{
          campanhaId: p.campanhaId,
          campanhaNome: campanha?.nome || p.campanhaId,
          configPersonalizada: configPers,
        }],
      });
    }
  });

  participanteCampanhaConfigs
    .filter((c) => c.empresaId === empresaId)
    .forEach((cfg) => {
      if (!map.has(cfg.participanteId)) {
        const partBase = participantesCampanhas.find((p) => p.id === cfg.participanteId);
        const campanha = campanhasBackoffice.find((c) => c.id === cfg.campanhaId);
        const configPers = temConfigPersonalizada(cfg.participanteId, cfg.campanhaId);

        if (partBase) {
          map.set(cfg.participanteId, {
            id: cfg.participanteId,
            nome: partBase.nome,
            email: partBase.email,
            cpf: partBase.cpf,
            status: partBase.status,
            pontosDisponiveis: partBase.pontosDisponiveis,
            pontosAcumulados: partBase.pontosAcumulados,
            nivel: partBase.nivel,
            campanhas: [{
              campanhaId: cfg.campanhaId,
              campanhaNome: campanha?.nome || cfg.campanhaId,
              configPersonalizada: configPers,
            }],
          });
        }
      } else {
        const existing = map.get(cfg.participanteId)!;
        const campanha = campanhasBackoffice.find((c) => c.id === cfg.campanhaId);
        const configPers = temConfigPersonalizada(cfg.participanteId, cfg.campanhaId);

        if (!existing.campanhas.find((cc) => cc.campanhaId === cfg.campanhaId)) {
          existing.campanhas.push({
            campanhaId: cfg.campanhaId,
            campanhaNome: campanha?.nome || cfg.campanhaId,
            configPersonalizada: configPers,
          });
        }
      }
    });

  return Array.from(map.values());
}

function getValorEfetivoCampanha(
  participanteId: string,
  campanhaId: string,
  campo: CampoPersonalizado
): { valor: number; personalizado: boolean; fonte: 'participante' | 'campanha' | 'empresa' } {
  const cfgPart = getConfigParticipanteCampanha(participanteId, campanhaId);

  if (cfgPart && cfgPart.ativo) {
    switch (campo) {
      case 'limiteResgateDiario':
        if (cfgPart.limiteResgateDiarioPersonalizado !== undefined) {
          return { valor: cfgPart.limiteResgateDiarioPersonalizado, personalizado: true, fonte: 'participante' };
        }
        break;
      case 'limiteResgateMensal':
        if (cfgPart.limiteResgateMensalPersonalizado !== undefined) {
          return { valor: cfgPart.limiteResgateMensalPersonalizado, personalizado: true, fonte: 'participante' };
        }
        break;
      case 'taxaProcessamentoPix':
        if (cfgPart.taxaProcessamentoPixPersonalizada !== undefined) {
          return { valor: cfgPart.taxaProcessamentoPixPersonalizada, personalizado: true, fonte: 'participante' };
        }
        break;
      case 'prazoAprovacaoSaque':
        if (cfgPart.prazoAprovacaoSaquePersonalizado !== undefined) {
          return { valor: cfgPart.prazoAprovacaoSaquePersonalizado, personalizado: true, fonte: 'participante' };
        }
        break;
    }
  }

  const configCampanha = configuracoesCampanhas.find((c) => c.campanhaId === campanhaId);
  if (configCampanha && !configCampanha.herdaPlataforma) {
    switch (campo) {
      case 'limiteResgateDiario': return { valor: configCampanha.limites.limite_resgate_diario, personalizado: false, fonte: 'campanha' };
      case 'limiteResgateMensal': return { valor: configCampanha.limites.limite_resgate_mensal, personalizado: false, fonte: 'campanha' };
      case 'taxaProcessamentoPix': return { valor: configCampanha.taxas.taxa_processamento_pix, personalizado: false, fonte: 'campanha' };
      case 'prazoAprovacaoSaque': return { valor: configCampanha.regras.prazo_aprovacao_saque, personalizado: false, fonte: 'campanha' };
    }
  }

  switch (campo) {
    case 'limiteResgateDiario': return { valor: valoresPadraoPlataforma.limites.limite_resgate_diario, personalizado: false, fonte: 'empresa' };
    case 'limiteResgateMensal': return { valor: valoresPadraoPlataforma.limites.limite_resgate_mensal, personalizado: false, fonte: 'empresa' };
    case 'taxaProcessamentoPix': return { valor: valoresPadraoPlataforma.taxas.taxa_processamento_pix, personalizado: false, fonte: 'empresa' };
    case 'prazoAprovacaoSaque': return { valor: valoresPadraoPlataforma.regras.prazo_aprovacao_saque, personalizado: false, fonte: 'empresa' };
  }

  return { valor: 0, personalizado: false, fonte: 'empresa' };
}

function getUltimaAlteracaoParticipante(participanteId: string): { data: string; alteradoPor: string } | null {
  const configs = getConfigsPorParticipante(participanteId).filter((c) => c.ativo);
  if (configs.length === 0) return null;
  const maisRecente = configs.reduce((a, b) =>
    new Date(a.ultimaAlteracao) > new Date(b.ultimaAlteracao) ? a : b
  );
  return { data: maisRecente.ultimaAlteracao, alteradoPor: maisRecente.alteradoPor };
}

function formatarUltimaAlteracao(dataISO: string): string {
  const data = new Date(dataISO);
  const agora = new Date();
  const diffMs = agora.getTime() - data.getTime();
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  const diffD = Math.floor(diffH / 24);

  if (diffH < 1) return 'Agora';
  if (diffH < 24) return `${diffH}h atrás`;
  if (diffD === 1) return 'Ontem';
  if (diffD < 7) return `${diffD} dias atrás`;
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export default function EmpresaPremiadosConfig({ empresa, onShowToast }: EmpresaPremiadosConfigProps) {
  const [participantes, setParticipantes] = useState<ParticipanteAgregado[]>(() => agruparParticipantesPorEmpresa(empresa.id));
  const [campanhaFiltro, setCampanhaFiltro] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [mostrarModalLote, setMostrarModalLote] = useState(false);
  const [mostrarModalDetalhe, setMostrarModalDetalhe] = useState(false);
  const [participanteDetalhe, setParticipanteDetalhe] = useState<ParticipanteAgregado | null>(null);
  const [campanhaDetalhe, setCampanhaDetalhe] = useState<string>('');
  const [loteValores, setLoteValores] = useState<Record<string, string>>();
  const [editValores, setEditValores] = useState<Record<string, string>>();

  const campanhasDaEmpresa = useMemo(() => {
    return campanhasBackoffice.filter((c) => c.empresaId === empresa.id);
  }, [empresa.id]);

  const participantesFiltrados = useMemo(() => {
    let lista = [...participantes];
    if (campanhaFiltro) {
      lista = lista.filter((p) => p.campanhas.some((c) => c.campanhaId === campanhaFiltro));
    }
    if (busca) {
      const b = busca.toLowerCase();
      lista = lista.filter(
        (p) =>
          p.nome.toLowerCase().includes(b) ||
          p.email.toLowerCase().includes(b) ||
          p.cpf.includes(b) ||
          p.id.toLowerCase().includes(b)
      );
    }
    return lista;
  }, [participantes, campanhaFiltro, busca]);

  const stats = useMemo(() => {
    return {
      total: participantesFiltrados.length,
      comConfigPersonalizada: participantesFiltrados.filter((p) =>
        p.campanhas.some((c) => c.configPersonalizada)
      ).length,
      ativos: participantesFiltrados.filter((p) => p.status === 'ativo').length,
      selecionadosCount: selecionados.size,
    };
  }, [participantesFiltrados, selecionados]);

  const toggleSelecionado = (id: string) => {
    const novo = new Set(selecionados);
    if (novo.has(id)) novo.delete(id);
    else novo.add(id);
    setSelecionados(novo);
  };

  const toggleTodos = () => {
    if (selecionados.size === participantesFiltrados.length) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(participantesFiltrados.map((p) => p.id)));
    }
  };

  const abrirDetalhe = (participante: ParticipanteAgregado, campanhaId?: string) => {
    setParticipanteDetalhe(participante);
    setCampanhaDetalhe(campanhaId || participante.campanhas[0]?.campanhaId || '');
    setEditValores({});
    setMostrarModalDetalhe(true);
  };

  const salvarConfigCampanha = (participanteId: string, campanhaId: string) => {
    const valores: Partial<ParticipanteCampanhaConfig> & { participanteId: string; campanhaId: string; empresaId: string; alteradoPor: string } = {
      participanteId,
      campanhaId,
      empresaId: empresa.id,
      alteradoPor: 'André Costa',
    };

    let temAlteracao = false;
    camposPersonalizaveis.forEach((campo) => {
      const val = editValores[campo.chave];
      if (val && val !== '') {
        temAlteracao = true;
        switch (campo.chave) {
          case 'limiteResgateDiario': valores.limiteResgateDiarioPersonalizado = Number(val); break;
          case 'limiteResgateMensal': valores.limiteResgateMensalPersonalizado = Number(val); break;
          case 'taxaProcessamentoPix': valores.taxaProcessamentoPixPersonalizada = Number(val); break;
          case 'prazoAprovacaoSaque': valores.prazoAprovacaoSaquePersonalizado = Number(val); break;
        }
      }
    });

    if (!temAlteracao) {
      removerConfigParticipanteCampanha(participanteId, campanhaId);
    } else {
      salvarConfigParticipanteCampanha(valores);
    }

    setParticipantes((prev) =>
      prev.map((p) => {
        if (p.id !== participanteId) return p;
        return {
          ...p,
          campanhas: p.campanhas.map((c) =>
            c.campanhaId === campanhaId
              ? { ...c, configPersonalizada: temAlteracao }
              : c
          ),
        };
      })
    );

    setMostrarModalDetalhe(false);
    setEditValores({});

    registrarAuditoria(
      'alterar_configuracao',
      'configuracoes',
      `${temAlteracao ? 'Personalizou' : 'Resetou'} configuração de ${participanteDetalhe?.nome} (${participanteId}) na campanha ${campanhaId}`,
      'André Costa',
      'admin',
      participanteId,
      'participante'
    );

    onShowToast(
      temAlteracao
        ? `Configuração de ${participanteDetalhe?.nome} na campanha salva com sucesso`
        : `Configuração de ${participanteDetalhe?.nome} resetada para padrão da campanha`,
      'success'
    );
  };

  const aplicarLote = () => {
    let count = 0;
    selecionados.forEach((partId) => {
      const part = participantes.find((p) => p.id === partId);
      if (!part) return;

      const targetCampanhaId = campanhaFiltro || part.campanhas[0]?.campanhaId;
      if (!targetCampanhaId) return;

      const valores: Partial<ParticipanteCampanhaConfig> & { participanteId: string; campanhaId: string; empresaId: string; alteradoPor: string } = {
        participanteId: partId,
        campanhaId: targetCampanhaId,
        empresaId: empresa.id,
        alteradoPor: 'André Costa',
      };

      let temAlteracao = false;
      camposPersonalizaveis.forEach((campo) => {
        const val = loteValores[campo.chave];
        if (val && val !== '') {
          temAlteracao = true;
          switch (campo.chave) {
            case 'limiteResgateDiario': valores.limiteResgateDiarioPersonalizado = Number(val); break;
            case 'limiteResgateMensal': valores.limiteResgateMensalPersonalizado = Number(val); break;
            case 'taxaProcessamentoPix': valores.taxaProcessamentoPixPersonalizada = Number(val); break;
            case 'prazoAprovacaoSaque': valores.prazoAprovacaoSaquePersonalizado = Number(val); break;
          }
        }
      });

      if (temAlteracao) {
        salvarConfigParticipanteCampanha(valores);
        count++;
      } else {
        removerConfigParticipanteCampanha(partId, targetCampanhaId);
      }
    });

    setParticipantes(agruparParticipantesPorEmpresa(empresa.id));
    setSelecionados(new Set());
    setMostrarModalLote(false);
    setLoteValores({});

    registrarAuditoria(
      'alterar_configuracao',
      'configuracoes',
      `Aplicou configuração em lote para ${count} participantes da empresa ${empresa.nome_fantasia}${campanhaFiltro ? ` na campanha ${campanhaFiltro}` : ''}`,
      'André Costa',
      'admin',
      empresa.id,
      'empresa'
    );

    onShowToast(`Configurações aplicadas em lote para ${count} participantes`, 'success');
  };

  return (
    <div className="space-y-5">
      {/* Filtros e stats */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="lg:w-72">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Campanha</label>
            <select
              value={campanhaFiltro}
              onChange={(e) => { setCampanhaFiltro(e.target.value); setSelecionados(new Set()); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
            >
              <option value="">Todas as campanhas</option>
              {campanhasDaEmpresa.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Buscar premiado</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                placeholder="Nome, email, CPF ou ID..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
          <span className="text-gray-500"><span className="font-bold text-gray-800">{stats.total}</span> premiados</span>
          <span className="text-gray-500"><span className="font-bold text-amber-600">{stats.comConfigPersonalizada}</span> com config. personalizada</span>
          <span className="text-gray-500"><span className="font-bold text-emerald-600">{stats.ativos}</span> ativos</span>
          {stats.selecionadosCount > 0 && (
            <span className="text-emerald-600 font-bold"><span className="font-bold">{stats.selecionadosCount}</span> selecionados</span>
          )}
        </div>
      </div>

      {/* Ações em lote */}
      {stats.selecionadosCount > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-emerald-600 text-sm"></i></div>
            </div>
            <p className="text-sm font-bold text-emerald-800">{stats.selecionadosCount} premiados selecionados</p>
          </div>
          <div className="flex items-center space-x-2">
            {campanhaFiltro && (
              <span className="text-xs text-emerald-600 font-medium">
                Aplicar na campanha: {campanhasDaEmpresa.find((c) => c.id === campanhaFiltro)?.nome}
              </span>
            )}
            <button
              onClick={() => setMostrarModalLote(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-sm"></i></div>
                <span>Configurar em Lote</span>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={participantesFiltrados.length > 0 && selecionados.size === participantesFiltrados.length}
                    onChange={toggleTodos}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Premiado</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Campanhas</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Pontuação</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {participantesFiltrados.map((p) => {
                const campanhasComConfig = p.campanhas.filter((c) => c.configPersonalizada).length;
                const ultimaAlteracao = getUltimaAlteracaoParticipante(p.id);
                return (
                  <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-all">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selecionados.has(p.id)}
                        onChange={() => toggleSelecionado(p.id)}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${campanhasComConfig > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                          {p.nome.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">{p.nome}</p>
                          <p className="text-[10px] text-gray-400">{p.id} · {p.email}</p>
                          {/* Badge de última alteração */}
                          {ultimaAlteracao && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] mt-1 px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 font-bold"
                              title={`Alterado por ${ultimaAlteracao.alteradoPor}`}
                            >
                              <i className="ri-time-line text-[9px]"></i>
                              {formatarUltimaAlteracao(ultimaAlteracao.data)}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold ${getStatusColor(p.status)}`}>{getStatusLabel(p.status)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-gray-700">
                          <span className="font-bold">{p.campanhas.length}</span> campanha{p.campanhas.length !== 1 ? 's' : ''}
                        </span>
                        {campanhasComConfig > 0 && (
                          <span className="text-[10px] text-amber-600 font-bold">
                            {campanhasComConfig} com config. personalizada
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs">
                        <span className="font-bold text-gray-800">{p.pontosDisponiveis.toLocaleString('pt-BR')}</span>
                        <span className="text-gray-400"> / {p.pontosAcumulados.toLocaleString('pt-BR')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => abrirDetalhe(p)}
                        className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-all cursor-pointer"
                        title="Ver configurações por campanha"
                      >
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-settings-3-line text-sm"></i></div>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {participantesFiltrados.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400 text-sm">
                    Nenhum premiado encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Mostrando <span className="font-bold">{participantesFiltrados.length}</span> de <span className="font-bold">{participantes.length}</span> premiados
          </p>
        </div>
      </div>

      {/* Modal detalhe por campanha */}
      {mostrarModalDetalhe && participanteDetalhe && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between z-10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">
                  {participanteDetalhe.nome.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{participanteDetalhe.nome}</h3>
                  <p className="text-xs text-gray-500">{participanteDetalhe.id} · {participanteDetalhe.email}</p>
                </div>
              </div>
              <button onClick={() => setMostrarModalDetalhe(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <div className="w-6 h-6 flex items-center justify-center"><i className="ri-close-line text-xl"></i></div>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Campanha</label>
                <div className="flex flex-wrap gap-2">
                  {participanteDetalhe.campanhas.map((c) => {
                    const efetivoCount = camposPersonalizaveis.filter((campo) => {
                      const v = getValorEfetivoCampanha(participanteDetalhe.id, c.campanhaId, campo.chave);
                      return v.personalizado;
                    }).length;
                    return (
                      <button
                        key={c.campanhaId}
                        onClick={() => { setCampanhaDetalhe(c.campanhaId); setEditValores({}); }}
                        className={`px-3 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                          campanhaDetalhe === c.campanhaId
                            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                            : 'bg-gray-50 border border-gray-100 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center space-x-1.5">
                          <span>{c.campanhaNome}</span>
                          {efetivoCount > 0 && (
                            <span className="w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[9px] flex items-center justify-center">
                              {efetivoCount}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {campanhaDetalhe && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">
                      Configurando <strong>{participanteDetalhe.nome}</strong> na campanha{' '}
                      <strong>{participanteDetalhe.campanhas.find((c) => c.campanhaId === campanhaDetalhe)?.campanhaNome}</strong>
                    </p>
                  </div>

                  {camposPersonalizaveis.map((campo) => {
                    const efetivo = getValorEfetivoCampanha(participanteDetalhe.id, campanhaDetalhe, campo.chave);
                    const cfgAtual = getConfigParticipanteCampanha(participanteDetalhe.id, campanhaDetalhe);
                    const valAtual =
                      campo.chave === 'limiteResgateDiario' ? cfgAtual?.limiteResgateDiarioPersonalizado :
                      campo.chave === 'limiteResgateMensal' ? cfgAtual?.limiteResgateMensalPersonalizado :
                      campo.chave === 'taxaProcessamentoPix' ? cfgAtual?.taxaProcessamentoPixPersonalizada :
                      cfgAtual?.prazoAprovacaoSaquePersonalizado;

                    return (
                      <div key={campo.chave} className={`rounded-lg border p-4 ${efetivo.personalizado ? 'border-amber-200 bg-amber-50/30' : 'border-gray-100 bg-white'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <label className="text-sm font-bold text-gray-800">{campo.label}</label>
                            <p className="text-[10px] text-gray-400">{campo.descricao}</p>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            efetivo.fonte === 'participante' ? 'bg-amber-100 text-amber-700' :
                            efetivo.fonte === 'campanha' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {efetivo.fonte === 'participante' ? 'Personalizado' : efetivo.fonte === 'campanha' ? 'Campanha' : 'Empresa'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                defaultValue={valAtual ?? ''}
                                placeholder={`Padrão: ${efetivo.valor}`}
                                onChange={(e) => {
                                  setEditValores((prev) => ({ ...prev, [campo.chave]: e.target.value }));
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                step={campo.tipo === 'porcentagem' ? '0.01' : '1'}
                              />
                              <span className="text-xs text-gray-400 whitespace-nowrap">{campo.unidade}</span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-gray-500">Valor efetivo:</p>
                            <p className={`text-sm font-bold ${efetivo.personalizado ? 'text-amber-600' : 'text-gray-800'}`}>
                              {campo.tipo === 'porcentagem' ? `${efetivo.valor}%` : `${campo.unidade} ${efetivo.valor.toLocaleString('pt-BR')}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                        <i className="ri-information-line text-blue-600 text-xs"></i>
                      </div>
                      <p className="text-xs text-blue-700">
                        Deixe um campo em branco para usar o valor da campanha/empresa.
                        Preencha para sobrescrever individualmente para este premiado nesta campanha.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-100 flex items-center justify-end space-x-3 z-10">
              <button
                onClick={() => {
                  setEditValores({});
                  setMostrarModalDetalhe(false);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => salvarConfigCampanha(participanteDetalhe.id, campanhaDetalhe)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                Salvar Configuração
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal aplicação em lote */}
      {mostrarModalLote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Configurar em Lote</h3>
              <button
                onClick={() => { setMostrarModalLote(false); setLoteValores({}); }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center"><i className="ri-close-line text-xl"></i></div>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {campanhaFiltro
                ? `Configurações serão aplicadas para ${stats.selecionadosCount} premiados selecionados na campanha "${campanhasDaEmpresa.find((c) => c.id === campanhaFiltro)?.name || campanhaFiltro}".`
                : `Configurações serão aplicadas para ${stats.selecionadosCount} premiados selecionados na primeira campanha de cada um.`}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Deixe um campo em branco para manter o valor atual de cada premiado.
            </p>
            <div className="space-y-4">
              {camposPersonalizaveis.map((campo) => (
                <div key={campo.chave}>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">{campo.label}</label>
                  <p className="text-[10px] text-gray-400 mb-1">{campo.descricao}</p>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={loteValores[campo.chave] || ''}
                      onChange={(e) => setLoteValores((prev) => ({ ...prev, [campo.chave]: e.target.value }))}
                      placeholder="Manter atual"
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                      step={campo.tipo === 'porcentagem' ? '0.01' : '1'}
                    />
                    <span className="text-xs text-gray-400">{campo.unidade}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setMostrarModalLote(false); setLoteValores({}); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={aplicarLote}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-sm"></i></div>
                  <span>Aplicar para {stats.selecionadosCount}</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}